import { TranslateService } from '@ngx-translate/core';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { AnyComponentData, DatabaseSourceComponentData } from '../../package.models';
import { ComponentTypeItem } from '../../../constants/component_types';
import { COMPONENT_TYPE } from '../../../constants/component_type';
import { updateComponent, updateRawComponent } from '../../store/component.actions';
import { AppState } from '../../../store';

@Component({
  selector: 'database-definition',
  template: `
    <div class="database-definition">
      <div class="row form-group">
        <div class="col-md-6" *ngIf="hasAccessMode">
          <label for="access_mode">{{
            'database-definition.form.' + component.componentType + '.labels.access_mode' | translate
          }}</label>
          <div class="btn-group btn-group-md btn-group-select">
            <button
              type="button"
              class="btn btn-default"
              value="table"
              name="access_mode"
              [ngClass]="{ 'active btn-primary': rawComponent.access_mode === 'table' }"
              (click)="onValueChange('table', 'access_mode')"
            >
              {{ 'database-definition.form.access_mode.options.table' | translate }}
            </button>
            <button
              type="button"
              class="btn btn-default"
              value="query"
              name="access_mode"
              [ngClass]="{ 'active btn-primary': rawComponent.access_mode === 'query' }"
              (click)="onValueChange('query', 'access_mode')"
            >
              {{ 'database-definition.form.access_mode.options.query' | translate }}
            </button>
          </div>
        </div>
      </div>
      <div class="row">
        <xp-form-group class="col-md-6" *ngIf="hasSchema">
          <label for="schema">{{
            'database-definition.form.' + component.componentType + '.labels.schema_name' | translate
          }}</label>
          <xp-input
            type="text"
            class="form-control"
            name="schema_name"
            id="schema_name"
            [ngModel]="rawComponent.schema_name"
            (ngModelChange)="onValueChange($event, 'schema_name')"
            placeholder="{{
              'database-definition.form.' + component.componentType + '.placeholders.schema_name' | translate
            }}"
          ></xp-input>
          <i
            class="fa fa-exclamation-circle form-control-info"
            matTooltip="Leave empty to use the user’s default schema."
            matTooltipPosition="right"
            *ngIf="
              component.componentType === 'database_source_component' ||
              component.componentType === 'amazon_redshift_source_component'
            "
          ></i>
        </xp-form-group>
      </div>
      <div class="row">
        <xp-form-group class="col-md-6" *ngIf="hasTable">
          <label for="table_name" *ngIf="hasTable && !hasCollection">{{
            'database-definition.form.' + component.componentType + '.labels.table_name' | translate
          }}</label>
          <label for="table_name" *ngIf="hasCollection">{{
            'database-definition.form.' + component.componentType + '.labels.collection' | translate
          }}</label>
          <xp-input
            type="text"
            class="form-control"
            name="table_name"
            id="table_name"
            [ngModel]="rawComponent.table_name"
            (ngModelChange)="onValueChange($event, 'table_name')"
            [placeholder]="
              (hasCollection
                ? 'database-definition.form.' + component.componentType + '.placeholders.collection'
                : 'database-definition.form.' + component.componentType + '.placeholders.table_name'
              ) | translate
            "
          ></xp-input>
        </xp-form-group>
      </div>
      <div class="row">
        <xp-form-group class="col-md-12" *ngIf="hasQuery">
          <label for="query">{{
            'database-definition.form.' + component.componentType + '.labels.query' | translate
          }}</label>
          <code-editor
            [value]="rawComponent.query"
            [options]="queryEditorOptions"
            name="query"
            (valueChange)="onValueChange($event, 'query')"
          ></code-editor>
        </xp-form-group>
        <xp-form-group class="col-md-12" *ngIf="hasWhereClause">
          <label for="where_clause">{{
            'database-definition.form.' + component.componentType + '.labels.where_clause' | translate
          }}</label>
          <code-editor
            [value]="rawComponent.where_clause"
            [options]="whereClauseEditorOptions"
            name="where_clause"
            (valueChange)="onValueChange($event, 'where_clause')"
          ></code-editor>
        </xp-form-group>
      </div>
    </div>
  `,
})
export class DatabaseDefinitionComponent implements OnInit, OnChanges {
  @Input() rawComponent: AnyComponentData;
  @Input() component: ComponentTypeItem;
  @Input() hasAccessMode = false;
  @Input() hasSchema = false;
  @Input() hasWhereClause = false;
  @Input() hasQuery = false;
  @Input() hasCollection = false;
  @Input() hasTable = true;
  @Input() whereClauseType = '';

  whereClauseEditorOptions: any = {
    placeholder: "prod_category = 1 AND prod_color = 'red'",
  };
  queryEditorOptions: any = {};

  constructor(private translate: TranslateService, private store: Store<AppState>) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.rawComponent) {
      if ((this.rawComponent as DatabaseSourceComponentData).access_mode === 'table') {
        this.hasTable = true;
        this.hasQuery = false;
        this.hasWhereClause = true;
      } else if ((this.rawComponent as DatabaseSourceComponentData).access_mode === 'query') {
        this.hasTable = false;
        this.hasSchema = false;
        this.hasWhereClause = false;
        this.hasQuery = true;
      }
    }
  }

  ngOnInit() {
    if (this.component.componentType === COMPONENT_TYPE.BIG_QUERY_SOURCE_COMPONENT) {
      this.queryEditorOptions = {
        placeholder:
          '/* Legacy SQL */ \nSELECT * FROM [project-id:dataset.table] \nSELECT * FROM dataset.table \n/* Standard SQL */ \nSELECT * from `project-id.dataset.table` \nSELECT * from project-id.dataset.table',
      };
    }

    if (this.whereClauseType === 'json') {
      this.whereClauseEditorOptions.mode = 'application/json';
      this.whereClauseEditorOptions.placeholder = '{"age":{"$gt":24}}';
    } else {
      this.whereClauseEditorOptions.placeholder = "prod_category = 1 AND prod_color = 'red'";
    }
  }

  onValueChange(value: any, key: string) {
    this.store.dispatch(
      updateRawComponent({
        rawComponent: { [key]: value },
      }),
    );
    this.store.dispatch(updateComponent({ component: { [key]: value } }));
  }
}
